import React from 'react';
import { Stethoscope } from 'lucide-react';
import { SectionCard, SectionFields, SectionFieldConfig, SectionFieldsProps, SectionTone, withOverrides } from './SectionCard';

/** Care plan keys this section writes (same names in every discipline's plan). */
export const ORDERING_PHYSICIAN_KEYS = [
  'orderingPhysicianName',
  'orderingPhysicianNPI',
  'orderingPhysicianPhone',
  'orderingPhysicianFax',
  'orderingPhysicianSpecialty',
  'orderingPhysicianAddressStreet',
  'orderingPhysicianAddressCity',
  'orderingPhysicianAddressState',
  'orderingPhysicianAddressZip',
] as const;

export type OrderingPhysicianKey = typeof ORDERING_PHYSICIAN_KEYS[number];
export type OrderingPhysicianValues = Record<OrderingPhysicianKey, string>;

export const emptyOrderingPhysician = (): OrderingPhysicianValues =>
  Object.fromEntries(ORDERING_PHYSICIAN_KEYS.map(k => [k, ''])) as OrderingPhysicianValues;

const FIELDS: SectionFieldConfig[] = [
  { key: 'orderingPhysicianName', label: 'Physician name', required: true, placeholder: 'Ordering physician' },
  { key: 'orderingPhysicianNPI', label: 'NPI', required: true, placeholder: '10-digit NPI', maxLength: 10 },
  { key: 'orderingPhysicianPhone', label: 'Phone', kind: 'tel' },
  { key: 'orderingPhysicianFax', label: 'Fax', kind: 'tel' },
  { key: 'orderingPhysicianSpecialty', label: 'Specialty', placeholder: 'e.g., Pediatrician' },
  { key: 'orderingPhysicianAddressStreet', label: 'Street address', wide: true },
  { key: 'orderingPhysicianAddressCity', label: 'City' },
  { key: 'orderingPhysicianAddressState', label: 'State', maxLength: 2 },
  { key: 'orderingPhysicianAddressZip', label: 'ZIP', maxLength: 10 },
];

const RECEIVED_POC_DATE: SectionFieldConfig = {
  key: 'physicianReceivedPOCDate', label: 'Date Physician Received POC', kind: 'date', wide: true,
  hint: 'Must be within 14 days of verbal order for CMS-485',
};

interface OrderingPhysicianSectionProps extends SectionFieldsProps {
  collapsed?: boolean;
  onToggle?: () => void;
  title?: string;
  tone?: SectionTone;
  /** Per-plan labels, placeholders and required marks, by key */
  overrides?: Partial<Record<OrderingPhysicianKey, Partial<SectionFieldConfig>>>;
  /** Adds the `physicianReceivedPOCDate` field (RN and PT plans) */
  showReceivedPOCDate?: boolean;
}

/**
 * Phase 19 F3/F25: the physician who signs the plan of care. Shared by the discipline care plans
 * so every plan names the signer the same way.
 */
const OrderingPhysicianSection: React.FC<OrderingPhysicianSectionProps> = ({
  collapsed = false, onToggle, title = 'Ordering physician', tone = 'cyan', overrides, showReceivedPOCDate = false, ...fieldProps
}) => {
  const fields = withOverrides(FIELDS, overrides);
  const name = fieldProps.values.orderingPhysicianName;
  return (
    <SectionCard title={title} icon={Stethoscope} tone={tone} collapsed={collapsed} onToggle={onToggle}
      summary={typeof name === 'string' ? name : undefined}>
      <SectionFields fields={showReceivedPOCDate ? [...fields, RECEIVED_POC_DATE] : fields} {...fieldProps} />
    </SectionCard>
  );
};

export default OrderingPhysicianSection;
